import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Shield, Scale, AlertTriangle, Edit, Save, Eye, History, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { id: 'privacy_policy', label: 'Privacy Policy', icon: Shield, description: 'How we collect and use data' },
  { id: 'terms_of_service', label: 'Terms of Service', icon: FileText, description: 'Rules for using our platform' },
  { id: 'legal_liability', label: 'Legal Liability', icon: Scale, description: 'Liability and indemnification' },
  { id: 'disclaimer', label: 'Disclaimer', icon: AlertTriangle, description: 'General disclaimers' },
  { id: 'cookie_policy', label: 'Cookie Policy', icon: FileText, description: 'How we use cookies' },
];

const LegalPage = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('privacy_policy');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editVersion, setEditVersion] = useState('');
  
  // Version history
  const [versions, setVersions] = useState([]);
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    // Check if user is admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.role === 'admin');
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // Try to get admin view first, fall back to public
      const token = getToken();
      let res;
      if (token) {
        try {
          res = await axios.get(`${API}/api/legal/documents`, { headers });
        } catch (err) {
          if (err.response?.status === 403) {
            res = await axios.get(`${API}/api/legal/documents/public`);
          } else {
            throw err;
          }
        }
      } else {
        res = await axios.get(`${API}/api/legal/documents/public`);
      }
      setDocuments(res.data);
      
      // Select the first document by default
      const firstDoc = res.data.find(d => d.document_type === activeTab);
      if (firstDoc) {
        setSelectedDoc(firstDoc);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async (docId) => {
    try {
      const res = await axios.get(`${API}/api/legal/documents/${docId}/versions`, { headers });
      setVersions(res.data);
      setVersionsModalOpen(true);
    } catch (err) {
      toast.error('Failed to load version history');
    }
  };

  const selectDocumentType = (docType) => {
    setActiveTab(docType);
    const doc = documents.find(d => d.document_type === docType);
    setSelectedDoc(doc || null);
    setEditMode(false);
  };

  const startEdit = () => {
    if (!selectedDoc) return;
    setEditContent(selectedDoc.content);
    setEditTitle(selectedDoc.title);
    setEditVersion(selectedDoc.version);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditContent('');
    setEditTitle('');
    setEditVersion('');
  };

  const saveDocument = async () => {
    if (!selectedDoc) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/api/legal/documents/${selectedDoc.id}`, {
        title: editTitle,
        content: editContent,
        version: editVersion
      }, { headers });
      
      toast.success('Document saved');
      setEditMode(false);
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const createDocument = async (docType) => {
    const docInfo = DOCUMENT_TYPES.find(d => d.id === docType);
    if (!docInfo) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/legal/documents`, {
        document_type: docType,
        title: docInfo.label,
        content: `# ${docInfo.label}\n\nContent coming soon...`,
        version: '1.0'
      }, { headers });
      
      toast.success('Document created');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  const publishDocument = async () => {
    if (!selectedDoc) return;
    
    try {
      await axios.post(`${API}/api/legal/documents/${selectedDoc.id}/publish`, {}, { headers });
      toast.success('Document published');
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to publish document');
    }
  };

  const unpublishDocument = async () => {
    if (!selectedDoc) return;
    
    try {
      await axios.post(`${API}/api/legal/documents/${selectedDoc.id}/unpublish`, {}, { headers });
      toast.success('Document unpublished');
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to unpublish document');
    }
  };

  const restoreVersion = async (versionId) => {
    if (!selectedDoc) return;
    
    try {
      await axios.post(`${API}/api/legal/documents/${selectedDoc.id}/restore/${versionId}`, {}, { headers });
      toast.success('Version restored');
      setVersionsModalOpen(false);
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to restore version');
    }
  };

  const initializeDefaults = async () => {
    try {
      await axios.post(`${API}/api/legal/initialize-defaults`, {}, { headers });
      toast.success('Default documents created');
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to initialize defaults');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="legal-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Scale className="h-6 w-6" />
            Legal & Compliance
          </h1>
          <p className="text-muted-foreground">View and manage legal documents</p>
        </div>
        {isAdmin && documents.length === 0 && (
          <Button onClick={initializeDefaults}>
            <Plus className="h-4 w-4 mr-1" />
            Initialize Default Documents
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Document List Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Documents</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-1">
              {DOCUMENT_TYPES.map((docType) => {
                const doc = documents.find(d => d.document_type === docType.id);
                const isActive = activeTab === docType.id;
                const Icon = docType.icon;
                
                return (
                  <button
                    key={docType.id}
                    onClick={() => selectDocumentType(docType.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isActive ? 'bg-slate-100' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`doc-${docType.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`h-5 w-5 mt-0.5 ${isActive ? 'text-slate-900' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                          {docType.label}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{docType.description}</p>
                        {doc && (
                          <div className="flex items-center gap-2 mt-1">
                            {doc.is_published ? (
                              <Badge className="text-xs bg-green-100 text-green-700">Published</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Draft</Badge>
                            )}
                            <span className="text-xs text-muted-foreground">v{doc.version}</span>
                          </div>
                        )}
                        {!doc && isAdmin && (
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="p-0 h-auto text-xs"
                            onClick={(e) => { e.stopPropagation(); createDocument(docType.id); }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Create
                          </Button>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Document Content */}
        <Card className="lg:col-span-3">
          {selectedDoc ? (
            <>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  {editMode ? (
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="text-xl font-semibold mb-2"
                    />
                  ) : (
                    <CardTitle className="text-xl">{selectedDoc.title}</CardTitle>
                  )}
                  <CardDescription className="flex items-center gap-2 mt-1">
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Version:</Label>
                        <Input
                          value={editVersion}
                          onChange={(e) => setEditVersion(e.target.value)}
                          className="h-7 w-20 text-xs"
                        />
                      </div>
                    ) : (
                      <>
                        <span>Version {selectedDoc.version}</span>
                        {selectedDoc.effective_date && (
                          <>
                            <span>•</span>
                            <span>Effective: {formatDate(selectedDoc.effective_date)}</span>
                          </>
                        )}
                      </>
                    )}
                  </CardDescription>
                </div>
                
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={saveDocument} disabled={loading}>
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => fetchVersions(selectedDoc.id)}>
                          <History className="h-4 w-4 mr-1" />
                          History
                        </Button>
                        <Button variant="outline" size="sm" onClick={startEdit}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        {selectedDoc.is_published ? (
                          <Button variant="outline" size="sm" onClick={unpublishDocument}>
                            <X className="h-4 w-4 mr-1" />
                            Unpublish
                          </Button>
                        ) : (
                          <Button size="sm" onClick={publishDocument}>
                            <Check className="h-4 w-4 mr-1" />
                            Publish
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[500px] font-mono text-sm"
                    placeholder="Enter markdown content..."
                  />
                ) : (
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="prose prose-slate max-w-none">
                      <ReactMarkdown>{selectedDoc.content}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center h-[500px] text-center">
              <FileText className="h-16 w-16 text-slate-200 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Document Selected</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select a document from the sidebar to view its content
              </p>
              {isAdmin && (
                <Button variant="outline" className="mt-4" onClick={initializeDefaults}>
                  <Plus className="h-4 w-4 mr-1" />
                  Initialize Default Documents
                </Button>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Version History Modal */}
      <Dialog open={versionsModalOpen} onOpenChange={setVersionsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              Previous versions of this document
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {versions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No version history available</p>
              ) : (
                versions.map((version) => (
                  <div key={version.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium">Version {version.version}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {formatDate(version.created_at)}
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => restoreVersion(version.id)}
                      >
                        Restore
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {version.content.substring(0, 200)}...
                    </p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegalPage;
